import { Injectable } from '@angular/core'
import { CourtroomDisplayInformation } from '@ftr/contracts/api/courtroom-display'
import { UserWithGlobalAdministratorRole } from '@ftr/contracts/api/user'
import { DisplayConfigRegistered } from '@ftr/contracts/type/courtroom-display'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, CookieStorageService, SimpleWindowRefService } from '@ftr/foundation'
import { Store } from '@ngxs/store'
import { Observable } from 'rxjs'
import { FeatureFlagKeyOfType, LaunchDarklyFlagValue, WebFeatureFlags } from '../feature-flags'
import { LaunchDarklySdkClientFactory } from '../launch-darkly-client-factory'
import { getIdentifyKeyForUser } from '../launch-darkly.utils'
import { FeatureFlagState } from '../store'

export const LD_PERSISTENT_ATTRIBUTE_STORAGE_KEY = 'ldPersistentAttribute'

@Injectable()
export class LaunchDarklyService {
  constructor(
    private readonly store: Store,
    private readonly launchDarklySdkClientFactory: LaunchDarklySdkClientFactory,
    private readonly cookieStorage: CookieStorageService,
    private readonly windowRefService?: SimpleWindowRefService,
  ) {}

  observeFlagChanges<T extends LaunchDarklyFlagValue>(key: FeatureFlagKeyOfType<T>): Observable<T> {
    return this.store.select(FeatureFlagState.featureFlag(key))
  }

  observeResolvedFlagChanges<T extends LaunchDarklyFlagValue>(key: FeatureFlagKeyOfType<T>): ApiResult<T | null> {
    return this.store.select(FeatureFlagState.resolvedFeatureFlagValue(key))
  }

  getFlagMap(): Observable<WebFeatureFlags> {
    return this.store.select(FeatureFlagState.allFeatureFlags)
  }

  /**
   * Calls the launch darkly api and returns the feature flag variation for a given user.
   * This method should be used in cases where the feature flag or user are not yet available
   * in the store, otherwise the store is the preferred method.
   */
  async getFlagVariationForUser<T extends LaunchDarklyFlagValue>(
    user: UserWithGlobalAdministratorRole,
    key: FeatureFlagKeyOfType<T>,
    courtSystemId?: string,
  ): Promise<T> {
    const ldClient = this.launchDarklySdkClientFactory.getClient()
    const isPersistent = this.cookieStorage.getGlobal<string>(LD_PERSISTENT_ATTRIBUTE_STORAGE_KEY) === 'true'
    await ldClient.identify({
      anonymous: false,
      email: user.email,
      firstName: user.givenName,
      lastName: user.familyName,
      key: getIdentifyKeyForUser({ userId: user.id, email: user.email }),
      custom: {
        userIsGlobalAdmin: user.isGlobalAdministrator,
        userId: user.id,
        ...this.getDomainAttributes(),
        ...(courtSystemId ? { courtSystemId } : {}),
        ...(isPersistent ? { isPersistent } : {}),
      },
    })
    return ldClient.variation(key)
  }

  /**
   * Calls the launch darkly api and returns the feature flag variation for an anonymous user.
   * This method should be used in cases where the user is not available such as public routes.
   * in the store, otherwise the store is the preferred method.
   */
  async getFlagVariationForAnonymous<T extends LaunchDarklyFlagValue>(
    key: FeatureFlagKeyOfType<T>,
    courtSystemId?: string,
  ): Promise<T> {
    const ldClient = this.launchDarklySdkClientFactory.getClient()
    const isPersistent = this.cookieStorage.getGlobal<string>(LD_PERSISTENT_ATTRIBUTE_STORAGE_KEY) === 'true'
    await ldClient.identify({
      anonymous: true,
      custom: {
        userIsGlobalAdmin: false,
        ...this.getDomainAttributes(),
        ...(courtSystemId ? { courtSystemId } : {}),
        ...(isPersistent ? { isPersistent } : {}),
      },
    })
    return ldClient.variation(key)
  }

  /**
   * Bypassing the store as we want to provide the court system ID from the URI,
   * also requires the user (if logged in) to provide any additional context
   */
  async getFlagVariationForPublicPages<T extends LaunchDarklyFlagValue>(
    key: FeatureFlagKeyOfType<T>,
    courtSystemId: Uuid,
    user: UserWithGlobalAdministratorRole | undefined,
  ): Promise<T> {
    if (user) {
      return this.getFlagVariationForUser<T>(user, key, courtSystemId)
    }

    return this.getFlagVariationForAnonymous<T>(key, courtSystemId)
  }

  private getDomainAttributes(): { domainName: string } | Record<string, never> {
    const domainName = this.windowRefService?.getHostName()
    return domainName ? { domainName } : {}
  }

  /**
   * Calls the launch darkly api and returns the feature flag variation for a given display application.
   */
  async getFlagVariationForDisplayApplication<T extends LaunchDarklyFlagValue>(
    registeredConfig: DisplayConfigRegistered,
    courtroomDisplayInformation: CourtroomDisplayInformation,
    key: FeatureFlagKeyOfType<T>,
    fallback: T,
  ): Promise<T> {
    const ldClient = this.launchDarklySdkClientFactory.getClient()

    await ldClient.identify({
      anonymous: false,
      kind: 'display-app',
      key: registeredConfig.displayId,
      custom: {
        courtroomId: courtroomDisplayInformation.courtroomId,
        courtSystemId: courtroomDisplayInformation.courtSystemId,
        region: registeredConfig.region,
      },
    })
    return ldClient.variation(key, fallback)
  }
}
